import { CheckCircle, Error, PlayCircleOutline, Warning } from '@mui/icons-material'
import store from 'state/store'
import { CURRENCY_PREPENDS } from 'utils/constants'
import { setDecimalPrecision } from 'utils/helpers'

export const syncObjectTypes = {
  PAYMENT: {
    id: 'PAYMENT',
    name: 'Payment',
    baseUrl: '/app/purchases/bills/',
  },
  IN_YARD_PAYMENT: {
    id: 'IN_YARD_PAYMENT',
    name: 'In Yard Payment',
    baseUrl: '/app/purchases/ticket-library/',
  },
  INVOICE: {
    id: 'INVOICE',
    name: 'Invoice',
    baseUrl: '/app/sales/invoices/',
  },
  SALES_ORDER: {
    id: 'SALES_ORDER',
    name: 'Sales Order',
    baseUrl: '/app/contracts/sales-orders/',
  },
  INBOUND_TICKET: {
    id: 'INBOUND_TICKET',
    name: 'Inbound Ticket',
    baseUrl: '/app/purchases/ticket-library/',
  },
  BILL: {
    id: 'BILL',
    name: 'Bill',
    baseUrl: '/app/purchases/bills/',
  },
  IN_YARD_BILL: {
    id: 'IN_YARD_BILL',
    name: 'In Yard Bill',
    baseUrl: '/app/purchases/ticket-library/',
  },
  FREIGHT_BILL: {
    id: 'FREIGHT_BILL',
    name: 'Freight Bill',
    baseUrl: '/app/freight/freight-bills/',
  },
}

export const getUrl = (objectType, data) => {
  if (objectType === syncObjectTypes.PAYMENT.id || objectType === syncObjectTypes.BILL.id) {
    const { inboundTicketId = null, billId = null } = data
    if (inboundTicketId) {
      return `${syncObjectTypes.INBOUND_TICKET.baseUrl}${inboundTicketId}/payment`
    }
    if (billId) return `${syncObjectTypes.BILL.baseUrl}${billId}`
  }
  if (objectType === syncObjectTypes.INVOICE.id) {
    return `${syncObjectTypes[objectType]?.baseUrl}${data.invoiceId}`
  }
  return `${syncObjectTypes[objectType]?.baseUrl}${data.id}`
}

/**
 * creates a lookup table for split payment lines
 * the key in the LUT is either a bill or IBT uuid
 * we do this so that we don't have to iterate over the entire api response in order to map each response item
 *
 * @param listItems
 * @returns {Map<string, array>}
 */
export function createSplitPaymentMap(listItems) {
  const splitPaymentMap = new Map()
  listItems.forEach(item => {
    const { objectType, data } = item
    // if bill payment, group payment lines by bill id
    if (objectType === syncObjectTypes.PAYMENT.id) {
      const group = splitPaymentMap.get(data?.billId) || []
      group.push(item)
      splitPaymentMap.set(data?.billId, group)
    }
    // if in yard payment, group payment lines by bill id
    if (objectType === syncObjectTypes.IN_YARD_PAYMENT.id) {
      const group = splitPaymentMap.get(data?.inboundTicketId) || []
      group.push(item)
      splitPaymentMap.set(data?.inboundTicketId, group)
    }
  })
  return splitPaymentMap
}

export function mapToSyncObject(item, splitPaymentMap) {
  const { data, objectType } = item
  const { defaultCurrency } = store.getState()?.settings || {}
  /*
   if object type is payment (bill or in yard) we actually want the id to be that
  of either the bill or the ibt so we can hyperlink to that screen
  */
  let billsAdjustedId = data.id
  let splitPmtList = []
  let adjustedRedirectUrl = `${syncObjectTypes[item.objectType]?.baseUrl}${data.id}`
  if (objectType === syncObjectTypes.PAYMENT.id) {
    billsAdjustedId = data.billId
    splitPmtList = splitPaymentMap.get(data.billId)
    adjustedRedirectUrl = `${syncObjectTypes[item.objectType]?.baseUrl}${data.billId}`
  }
  if (objectType === syncObjectTypes.IN_YARD_PAYMENT.id) {
    billsAdjustedId = data.inboundTicketId
    splitPmtList = splitPaymentMap.get(data.inboundTicketId)
    adjustedRedirectUrl = `${syncObjectTypes[item.objectType]?.baseUrl}${data.inboundTicketId}/payment`
  }
  if (objectType === syncObjectTypes.IN_YARD_BILL.id) {
    billsAdjustedId = data.inboundTicketId
    adjustedRedirectUrl = `${syncObjectTypes[item.objectType]?.baseUrl}${data.inboundTicketId}`
  }

  const pricePrepend =
    !item.data?.currency || defaultCurrency === item.data?.currency
      ? CURRENCY_PREPENDS[defaultCurrency]?.hc
      : CURRENCY_PREPENDS[item.data?.currency]?.fc

  return {
    id: billsAdjustedId,
    itemId: item.data?.id,
    ticketNumber: Number(item.data?.number),
    redirectUrl: adjustedRedirectUrl,
    objectType: item.objectType,
    objectTypeId: syncObjectTypes[item.objectType]?.id || '',
    customer: item.data?.customer?.customerCommonIdentifierString,
    dateCreated: item.data?.date,
    value: `${pricePrepend}${setDecimalPrecision(item.data?.total, {
      decimalPrecisionOverride: 2,
    })}`,
    syncStatus: item.syncStatus,
    itemsLinked: splitPmtList,
    pricePrepend,
  }
}

export const alertTypes = {
  SUCCEEDED: {
    id: 'SUCCEEDED',
    severity: 'success',
    message: 'Sync is complete',
  },
  FAILED_ALL: {
    id: 'FAILED_ALL',
    severity: 'error',
    message: 'There was an error syncing items',
  },
  SUCCESS_PARTIAL: {
    id: 'SUCCESS_PARTIAL',
    severity: 'warning',
    message: 'Some items failed to sync',
  },
  SYNC_IN_PROGRESS: {
    id: 'IN_PROGRESS',
    severity: 'success',
    message: 'Sync has been initiated, check back and refresh in a few minutes',
  },
}

export const syncStatus = {
  COMPLETED_BY_SYSTEM: {
    icon: () => <CheckCircle color="success" sx={{ mr: 1 }} />,
    text: 'Synced',
  },
  FAILED: {
    icon: () => <Error color="error" sx={{ mr: 1 }} />,
    text: 'Failed',
  },
  COMPLETED_BY_USER: {
    icon: () => <CheckCircle color="success" sx={{ mr: 1 }} />,
    text: 'Completed by user',
  },
  IN_PROGRESS: {
    icon: () => <PlayCircleOutline color="info" sx={{ mr: 1 }} />,
    text: 'In Progress',
  },
}

export const batchStatus = {
  SYNC_COMPLETED: {
    icon: () => <CheckCircle color="success" sx={{ mr: 1 }} />,
    text: 'Success',
  },
  SYNC_PARTIALLY_COMPLETE: {
    icon: () => <Warning color="warning" sx={{ mr: 1 }} />,
    text: 'Some items failed to sync',
  },
  SYNC_FAILED: {
    icon: () => <Error color="error" sx={{ mr: 1 }} />,
    text: 'Failed',
  },
  IN_PROGRESS: {
    icon: () => <PlayCircleOutline color="info" sx={{ mr: 1 }} />,
    text: 'In Progress',
  },
}
